/**
 * Time calculation utilities
 */

export interface TimeUnit {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Calculates the time difference between two dates
 * @param targetDate - The target date
 * @param currentDate - The current date (defaults to now)
 * @returns Time difference broken down into units
 */
export const calculateTimeDifference = (
  targetDate: Date,
  currentDate: Date = new Date(),
): TimeUnit => {
  const timeDiff = targetDate.getTime() - currentDate.getTime();

  if (timeDiff <= 0) {
    return {
      days: 0, hours: 0, minutes: 0, seconds: 0,
    };
  }

  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

  return {
    days, hours, minutes, seconds,
  };
};

/**
 * Calculates days difference between two dates
 * @param targetDate - The target date
 * @param currentDate - The current date (defaults to now)
 * @returns Number of days difference
 */
export const calculateDaysDifference = (
  targetDate: Date,
  currentDate: Date = new Date(),
): number => Math.floor(
  (targetDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24),
);

/**
 * Converts Unix timestamp to Date object
 * @param timestamp - Unix timestamp in seconds
 * @returns Date object
 */
export const timestampToDate = (timestamp: number): Date => new Date(timestamp * 1000);
